/**
 * The versionedPlayEngine build endpoints — export, demo link, batch — which
 * run in the agentContainer and therefore as background jobs.
 *
 * Each call answers like the old synchronous chain would have (`{ ok, status,
 * data | error }`), with progress events in between; the job plumbing is
 * jobsApi. The status route is this router's own, because a job on a
 * versionedPlayEngine base is authorized through the base's grant, not through
 * the snapshot editor's.
 */

import { postJson, runJob, JOB_CONNECTION_LOST, JOB_LOST, JOB_NOT_STARTED } from "./jobsApi";

const STATUS_PATH = "versionPlayEngine/jobStatus";

function start(servicePath, payload, hooks) {
  return runJob(servicePath, payload, hooks, { statusPath: STATUS_PATH });
}

/**
 * Networks × languages, composed with the company build profile, one zip.
 *
 * `versionIDs` makes it a folder export: the same networks and languages are
 * applied to every version named, each composed exactly as its own single
 * download would be, gathered under `folderName` and zipped once.
 *
 * @param {object} params
 * @param {string} params.baseID
 * @param {string} params.versionID      the version on screen; leads the folder
 * @param {string[]} params.networks     the keys the dialog shows (`web`, `applovin`…)
 * @param {string[]} params.languages    codes, or `["all"]` for device-language mode
 * @param {string[]} [params.versionIDs] the folder's versions, when exporting a folder
 * @param {string} [params.folderName]   names the directory the folder unzips to
 * @param {boolean} [params.subsetFonts]
 * @param {string} [params.exportName]   deliver under exactly this name
 * @param {string} [params.gameTitle]    the name the profile's `{gameTitle}` resolves to
 * @returns {Promise<{ok, status, data: {file, fileName, exported, noFolderCancelledNetworks, failed, warnings, engine}}>}
 */
/** What the version would export to, in bytes; `estimatedExportBytes` is null without the engine checkout. */
export const versionSizeEstimate = (versionID) =>
  postJson("versionPlayEngine/sizeEstimate", { versionID: String(versionID) });

export function exportVersion(
  { baseID, versionID, networks, languages, versionIDs, folderName, subsetFonts, exportName, gameTitle, deliverToDropbox, dropboxFolder },
  hooks
) {
  return start(
    "versionPlayEngine/exportVersion",
    {
      baseID: String(baseID),
      versionID: String(versionID),
      networks,
      languages,
      ...(versionIDs && versionIDs.length ? { versionIDs: versionIDs.map(String) } : {}),
      ...(folderName ? { folderName } : {}),
      ...(subsetFonts === false ? { subsetFonts: false } : {}),
      ...(exportName ? { exportName } : {}),
      ...(gameTitle ? { gameTitle } : {}),
      // Only sent when asked for; the server treats anything else as "no".
      ...(deliverToDropbox === true ? { deliverToDropbox: true } : {}),
      // The folder picked in the Send dialog, when it is not the game's own.
      ...(dropboxFolder ? { dropboxFolder } : {}),
    },
    hooks
  );
}

/**
 * One default-network build uploaded for demo.playin.com.tr.
 *
 * @returns {Promise<{ok, status, data: {file: {Location, Key}, previewData: {fileName, timeStamp, language}}}>}
 */
export function previewVersion({ baseID, versionID, language, loadingScreen, subsetFonts }, hooks) {
  return start(
    "versionPlayEngine/previewVersion",
    {
      baseID: String(baseID),
      versionID: String(versionID),
      ...(language ? { language } : {}),
      ...(loadingScreen ? { loadingScreen } : {}),
      ...(subsetFonts === false ? { subsetFonts: false } : {}),
    },
    hooks
  );
}

/**
 * One sentence for a `{ ok: false }` answer, whatever shape the refusal took:
 * a bare code (`USER_HAS_NO_EXPORT_PERMISSION`), the validator's `{ message }`
 * with its list, a job's `{ error }`, or a lost connection.
 */
export function describeExportFailure(result, fallback = "Export failed") {
  const error = result && result.error;
  if (!error) return fallback;
  if (typeof error === "string") {
    if (error === JOB_CONNECTION_LOST) return "The connection to the server was lost; the build kept running there.";
    if (error === JOB_LOST) return "The server restarted while this was running. Try again.";
    if (error === JOB_NOT_STARTED) return "The request never reached the server. Check your connection and try again.";
    return error;
  }
  const code = error.error || error.message;
  const list = error.unsupportedNetworks || error.unsupportedLanguages;
  if (code && Array.isArray(list) && list.length) return `${code}: ${list.join(", ")}`;
  const failures = Array.isArray(error.failed) ? error.failed : [];
  if (code && failures.length) {
    const first = failures[0];
    return `${code} — ${first.language || ""} ${first.network || ""}: ${first.error || "unknown"}`.replace(/\s+/g, " ");
  }
  return code || fallback;
}

/**
 * The container reports each cell of the matrix as it lands; this is the
 * "3 of 8 built" a dialog shows while it waits. `progress` is the whole list
 * the job has reported so far.
 */
export function countBuildProgress(progress) {
  let built = 0;
  let failed = 0;
  let total = null;
  for (const event of progress || []) {
    if (event.type === "built") built += 1;
    else if (event.type === "build_failed") failed += 1;
    else if (event.type === "stage" && event.stage === "build" && typeof event.total === "number") total = event.total;
  }
  return { built, failed, total };
}

/**
 * The versionedPlayEngine stages worth a line of their own while the dialogs
 * wait — see BuildWaitLine. The snapshot editor dialogs describe their stages
 * themselves and pass none.
 */
export const VERSION_BUILD_STAGE_LABELS = Object.freeze({
  prepare: "Preparing the version's assets",
  build: "Sending to the build server",
  package: "Packaging",
  upload: "Uploading",
});
